import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faExclamationTriangle, faFilm, faTv, faCalendarAlt, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { ReportsAPI } from '../../api';
import { useNavigate } from 'react-router-dom';

const ReportsContainer = styled.div`
  margin-bottom: 40px;
`;

const ReportsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ReportItem = styled.div`
  background-color: rgba(26, 26, 26, 0.8);
  border-radius: 8px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  cursor: default;
  
  &:hover {
    background-color: rgba(42, 42, 42, 0.8);
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  }
`;

const ReportHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const ReportTitle = styled.h3`
  font-size: 18px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  font-weight: 500;
  
  svg {
    color: ${props => props.$contentType === 'movie' ? '#ff9800' : '#3b82f6'};
  }
  
  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const ReportDate = styled.div`
  color: var(--text-dim);
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ReportStatus = styled.div`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => {
    switch (props.$status) {
      case 'resolved':
        return 'rgba(72, 187, 120, 0.2)';
      case 'pending':
        return 'rgba(237, 137, 54, 0.2)';
      case 'rejected':
        return 'rgba(229, 62, 62, 0.2)';
      default:
        return 'rgba(160, 174, 192, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'resolved':
        return '#48bb78';
      case 'pending':
        return '#ed8936';
      case 'rejected':
        return '#e53e3e';
      default:
        return '#a0aec0';
    }
  }};
  margin-left: 10px;
`;

const ReportReason = styled.div`
  margin-bottom: 10px;
  font-weight: 500;
`;

const ReportMessage = styled.div`
  color: var(--text-dim);
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  margin-bottom: 10px;
  display: flex;
  flex-wrap: wrap;
  
  &:empty {
    display: none;
  }
`;

const ReportSource = styled.div`
  font-size: 13px;
  color: var(--text-dim);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 10px;
  margin-top: 10px;
  display: ${props => props.$hasSource ? 'block' : 'none'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  background-color: rgba(26, 26, 26, 0.8);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  
  h3 {
    font-size: 20px;
    margin-bottom: 10px;
  }
  
  p {
    color: var(--text-dim);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  
  svg {
    font-size: 30px;
    color: var(--primary-light);
    animation: spin 1s linear infinite;
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
`;

const PageButton = styled.button`
  background-color: ${props => props.$active ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$active ? 'white' : 'var(--text-dim)'};
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.5 : 1};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.$active ? 'var(--primary-light)' : 'rgba(255, 255, 255, 0.2)'};
  }
`;

const UserReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dataFetched, setDataFetched] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    // Only fetch data if it hasn't been fetched before
    if (!dataFetched) {
      fetchReports();
    }
  }, [page, dataFetched]);
  
  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await ReportsAPI.getUserReports({
        page,
        limit: 5
      });
      
      if (response.data && response.data.success) {
        console.log('Reports API response:', response.data);
        const reportsData = response.data.data.reports || [];
        console.log('Reports data:', reportsData);
        
        setReports(reportsData);
        setTotalPages(response.data.data.total_pages || 1);
        setDataFetched(true);
      } else {
        setError('Failed to load reports');
        setReports([]);
      }
    } catch (err) {
      console.error('Error fetching user reports:', err);
      setError('Failed to load reports. Please try again later.');
      setReports([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleContentClick = (report) => {
    if (report.content_type === 'movie') {
      navigate(`/movie/${report.content_id}`);
    } else {
      navigate(`/show/${report.content_id}`);
    }
  };
  
  const formatDate = (dateString) => {
    try {
      if (!dateString) {
        return 'Unknown date';
      }
      
      // Parse the UTC date string
      const date = new Date(dateString);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'Unknown date';
      }
      
      // Add 5 hours and 30 minutes for IST adjustment
      const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
      
      // Format using browser's locale settings with IST time
      return istDate.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }) + ' IST';
    } catch (err) {
      console.error('Error formatting date:', err, dateString);
      return 'Unknown date';
    }
  };
  
  const getStatusLabel = (status) => {
    switch (status) {
      case 'resolved':
        return 'Resolved';
      case 'rejected':
        return 'Rejected';
      case 'pending':
      default:
        return 'Pending';
    }
  };
  
  if (loading) {
    return (
      <LoadingContainer>
        <FontAwesomeIcon icon={faSpinner} />
      </LoadingContainer>
    );
  }
  
  if (error) {
    return (
      <EmptyState>
        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" color="#e53e3e" />
        <h3>Error Loading Reports</h3>
        <p>{error}</p>
      </EmptyState>
    );
  }
  
  if (reports.length === 0) {
    return (
      <EmptyState>
        <h3>No Reports Found</h3>
        <p>You haven't submitted any content reports yet.</p>
      </EmptyState>
    );
  }
  
  return (
    <ReportsContainer>
      <ReportsList>
        {reports.map(report => (
          <ReportItem key={report._id}>
            <ReportHeader>
              <ReportTitle 
                $contentType={report.content_type}
                onClick={() => handleContentClick(report)}
              >
                <FontAwesomeIcon 
                  icon={report.content_type === 'movie' ? faFilm : faTv} 
                />
                {report.content_title || `${report.content_type === 'movie' ? 'Movie' : 'Show'} Report`}
                <ReportStatus $status={report.status || 'pending'}>
                  {getStatusLabel(report.status)}
                </ReportStatus>
              </ReportTitle>
              
              <ReportDate>
                <FontAwesomeIcon icon={faCalendarAlt} />
                {formatDate(report.created_at || report.createdAt)}
              </ReportDate>
            </ReportHeader>
            
            <ReportReason>
              {report.reason_label || report.reason || 'General Issue'}
            </ReportReason>
            
            {report.custom_message && (
              <ReportMessage>
                {report.custom_message}
              </ReportMessage>
            )}
            
            <ReportSource $hasSource={!!report.source_url}>
              <FontAwesomeIcon icon={faExternalLinkAlt} /> Source reported
            </ReportSource>
          </ReportItem>
        ))}
      </ReportsList>
      
      {totalPages > 1 && (
        <Pagination>
          <PageButton 
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </PageButton>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
            <PageButton 
              key={pageNum}
              $active={pageNum === page}
              onClick={() => setPage(pageNum)}
            >
              {pageNum}
            </PageButton>
          ))}
          
          <PageButton 
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </PageButton>
        </Pagination>
      )}
    </ReportsContainer>
  );
};

export default UserReports; 